import { useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import type { Job } from "@/data/types";
import type { ChangeOrder, ChangeOrderLineItem } from "@/data/changeOrderTypes";
import { getNextChangeOrderNumber } from "@/data/changeOrderTypes";
import { SignatureCapture } from "@/components/SignatureCapture";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, FileText, PenTool, ArrowRight,
} from "lucide-react";
import { useJobDetailQuery } from "@/hooks/useJobDetailQuery";
import { useCatalog } from "@/data/CatalogContext";
import type { CatalogVariant } from "@/data/CatalogContext";

type Step = "details" | "signatures" | "confirm";

interface JobMaterialForCO {
  id: string;
  sku: string;
  item_name: string;
  prices_snapshot: number;
  quantity_required: number;
}

interface MaterialLineRow extends ChangeOrderLineItem {
  id: string;
  removeMaterialId: string | null;
  addMaterialSku: string | null;
  quantity: number;
  actionType: "replace" | "add" | "remove_only";
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  existingOrders: ChangeOrder[];
  onSubmit: (co: ChangeOrder) => void | Promise<void>;
}

export function ChangeOrderDialog({ open, onOpenChange, job, existingOrders, onSubmit }: Props) {
  const [step, setStep] = useState<Step>("details");
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState<MaterialLineRow[]>([
    {
      id: 'row-init-0',
      description: "", amount: 0, quantity: 1, actionType: "replace",
      removeMaterialId: null, addMaterialSku: null,
    },
  ]);
  const [homeownerSig, setHomeownerSig] = useState<string | null>(null);
  const [repSig, setRepSig] = useState<string | null>(null);
  // Separate from changeAmount/revisedContractValue (customer-facing): what the
  // INSTALLER is owed for this change order, if anything. Manually entered —
  // there's no reliable way to derive it from customer pricing (see
  // canonicalSyncGuardrail.test.ts), and not every change order involves the
  // installer (e.g. a pure price adjustment with no extra installer work).
  const [installerPayoutDelta, setInstallerPayoutDelta] = useState(0);

  // Fetch job materials and catalog data
  const { jobMaterials, loading } = useJobDetailQuery(open ? job.id : undefined);
  const { skuCatalog, isLoading: catalogLoading } = useCatalog();

  // Filter job materials to those with valid SKU and quantity
  const validJobMaterials: JobMaterialForCO[] = jobMaterials
    .filter((mat) => mat.sku && Number(mat.quantity_required) > 0)
    .map(mat => ({
      id: mat.id as string,
      sku: mat.sku as string,
      item_name: mat.item_name as string,
      quantity_required: Number(mat.quantity_required),
      prices_snapshot: mat.prices_snapshot !== undefined && mat.prices_snapshot !== null
        ? Number(mat.prices_snapshot)
        : 0,
    }));

  // Filter catalog to active variants
  const activeCatalogItems = skuCatalog.filter(item => item.active);

  // Helper: get material objects (memoized for stable references)
  const getRemoveMaterial = useCallback((row: MaterialLineRow) =>
    validJobMaterials.find(m => m.id === row.removeMaterialId),
    [validJobMaterials]
  );
  const getAddMaterial = useCallback((row: MaterialLineRow) =>
    activeCatalogItems.find(i => i.sku === row.addMaterialSku),
    [activeCatalogItems]
  );

  // Compute derived values per row (memoized)
  const computeRow = useCallback((row: MaterialLineRow) => {
    const removeMat = getRemoveMaterial(row);
    const addMat = getAddMaterial(row);
    const unitDelta = (addMat?.unitCost ?? 0) - (removeMat?.prices_snapshot ?? 0);
    const amount = unitDelta * row.quantity;
    const description = (() => {
      switch (row.actionType) {
        case "replace":
          return `Replace ${removeMat?.item_name || "material"} with ${addMat?.itemName || "new material"}`;
        case "add":
          return `Add ${addMat?.itemName || "material"}`;
        case "remove_only":
          return `Remove ${removeMat?.item_name || "material"}`;
      }
    })();
    return { description, amount, unitDelta, removeMat, addMat };
  }, [getRemoveMaterial, getAddMaterial]);

  // Enriched rows with computed fields
  const enrichedLineItems = useMemo(() =>
    lineItems.map(row => ({ ...row, ...computeRow(row) })),
    [lineItems, computeRow]
  );

  // Get IDs of materials already selected for removal in other rows
  const getSelectedRemoveMaterialIds = (currentIdx: number): string[] => {
    return lineItems
      .map((row, idx) => (idx !== currentIdx ? row.removeMaterialId : null))
      .filter((id): id is string => id !== null);
  };

  const changeAmount = useMemo(() =>
    enrichedLineItems
      .filter(row => {
        if (row.actionType === "replace") return row.removeMat && row.addMat;
        if (row.actionType === "add") return row.addMat;
        if (row.actionType === "remove_only") return row.removeMat;
        return false;
      })
      .reduce((sum, row) => sum + row.amount, 0),
    [enrichedLineItems]
  );
  const currentContractValue = job.payoutAmount;
  const revisedTotal = currentContractValue + changeAmount;

  const updateRow = (idx: number, patch: Partial<MaterialLineRow>) => {
    setLineItems(prev => prev.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };

  const addRow = () => {
    setLineItems(prev => [
      ...prev,
      {
        id: `row-${Date.now()}`,
        description: "", amount: 0, quantity: 1, actionType: "replace",
        removeMaterialId: null, addMaterialSku: null,
      },
    ]);
  };

  const removeRow = (idx: number) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleActionTypeChange = (idx: number, actionType: "replace" | "add" | "remove_only") => {
    const patch: Partial<MaterialLineRow> = { actionType };
    if (actionType === "add") {
      patch.removeMaterialId = null;
    } else if (actionType === "remove_only") {
      patch.addMaterialSku = null;
    }
    updateRow(idx, patch);
  };

  const handleRemoveMaterialChange = (idx: number, materialId: string | null) => {
    const row = lineItems[idx];
    updateRow(idx, { removeMaterialId: materialId });
    if (materialId) {
      const mat = validJobMaterials.find(m => m.id === materialId);
      if (mat) {
        updateRow(idx, { quantity: mat.quantity_required });
      }
    }
  };

  const handleAddMaterialChange = (idx: number, materialSku: string | null) => {
    updateRow(idx, { addMaterialSku: materialSku });
  };

  const handleQuantityChange = (idx: number, quantity: number) => {
    const clampedQty = Math.max(0.5, quantity);
    updateRow(idx, { quantity: clampedQty });
  };

  const hasValidMaterialRow = lineItems.some(row => {
    if (row.actionType === "replace") return row.removeMaterialId && row.addMaterialSku;
    if (row.actionType === "add") return !!row.addMaterialSku;
    if (row.actionType === "remove_only") return !!row.removeMaterialId;
    return false;
  });
  // A change order doesn't have to touch materials at all — e.g. "one more
  // day of labor" has no material line, but does have an installer payout
  // impact. Require at least one of the two so an empty change order can't
  // be signed.
  const canProceedToSign =
    summary.trim().length > 0 && (hasValidMaterialRow || installerPayoutDelta !== 0);

  const canFinalize = !!homeownerSig;

  // Group totals (only include rows with both materials selected where required)
  const replaceTotal = useMemo(() =>
    enrichedLineItems
      .filter(r => r.actionType === "replace" && r.removeMat && r.addMat)
      .reduce((sum, r) => sum + r.amount, 0),
    [enrichedLineItems]
  );
  const addTotal = useMemo(() =>
    enrichedLineItems
      .filter(r => r.actionType === "add" && r.addMat)
      .reduce((sum, r) => sum + r.amount, 0),
    [enrichedLineItems]
  );
  const removeTotal = useMemo(() =>
    Math.abs(enrichedLineItems
      .filter(r => r.actionType === "remove_only" && r.removeMat)
      .reduce((sum, r) => sum + r.amount, 0)
    ),
    [enrichedLineItems]
  );

  const handleFinalize = async () => {
    const now = new Date().toISOString();

    // Filter to only complete rows
    const validRows = lineItems.filter(row => {
      if (row.actionType === "replace") return row.removeMaterialId && row.addMaterialSku;
      if (row.actionType === "add") return row.addMaterialSku;
      if (row.actionType === "remove_only") return row.removeMaterialId;
      return false;
    });

    const co: ChangeOrder = {
      id: `CO-${Date.now()}`,
      jobId: job.id,
      orderNumber: getNextChangeOrderNumber(existingOrders),
      status: "Signed",
      summary,
      lineItems: validRows.map(row => ({
        description: computeRow(row).description,
        amount: computeRow(row).amount,
        quantity: row.quantity,
        actionType: row.actionType,
      })),
      originalContractValue: currentContractValue,
      changeAmount,
      revisedContractValue: revisedTotal,
      installerPayoutDelta,
      homeownerSignature: homeownerSig ?? undefined,
      homeownerSignedAt: homeownerSig ? now : undefined,
      repSignature: repSig ?? undefined,
      repSignedAt: repSig ? now : undefined,
      createdAt: now,
      createdBy: "Current User",
      signedAt: now,
      sentToCustomerAt: now,
      notes,
    };
    await onSubmit(co);
    toast.success(`Change Order CO-${co.orderNumber} signed and recorded`);
    // Reset
    setStep("details");
    setSummary("");
    setNotes("");
    setLineItems([{
      id: 'row-init-0',
      description: "", amount: 0, quantity: 1, actionType: "replace",
      removeMaterialId: null, addMaterialSku: null,
    }]);
    setInstallerPayoutDelta(0);
    setHomeownerSig(null);
    setRepSig(null);
    onOpenChange(false);
  };

  if (loading || catalogLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2">Loading...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            New Change Order — {job.jobNumber || job.id}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-2">
          {(["details", "signatures", "confirm"] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border ${
                step === s ? "bg-primary text-primary-foreground border-primary" :
                (["details", "signatures", "confirm"].indexOf(step) > i)
                  ? "bg-success/20 text-success border-success/30"
                  : "bg-muted text-muted-foreground border-border"
              }`}>
                {i + 1}
              </div>
              <span className={`text-xs font-medium capitalize ${step === s ? "text-foreground" : "text-muted-foreground"}`}>
                {s === "details" ? "Scope & Pricing" : s === "signatures" ? "Signatures" : "Confirm"}
              </span>
              {i < 2 && <ArrowRight className="h-3 w-3 text-muted-foreground mx-1" />}
            </div>
          ))}
        </div>

        {/* ── STEP: DETAILS ── */}
        {step === "details" && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Change Order Summary *</Label>
              <Textarea
                placeholder="Describe the scope change (e.g., upgrade wall material from Sentrel to Dekton)"
                value={summary}
                onChange={e => setSummary(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label className="text-xs mb-2 block">Line Items</Label>
              <div className="space-y-3">
                {enrichedLineItems.map((row, idx) => {
                  const selectedRemoveMaterialIds = getSelectedRemoveMaterialIds(idx);
                  const priceSnapWarning = row.removeMat && (row.removeMat.prices_snapshot === null || row.removeMat.prices_snapshot === 0);
                  const catalogWarning = row.addMat && (row.addMat.unitCost === null || row.addMat.unitCost === 0);

                  return (
                    <div key={row.id} className="rounded-lg border p-3 bg-card space-y-2">
                      {/* Row header: action type + delete */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs font-semibold">Action</Label>
                          <select
                            value={row.actionType}
                            onChange={(e) => handleActionTypeChange(idx, e.target.value as "replace" | "add" | "remove_only")}
                            className="text-xs px-2 py-1 border rounded"
                          >
                            <option value="replace">Replace</option>
                            <option value="add">Add Only</option>
                            <option value="remove_only">Remove Only</option>
                          </select>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={() => removeRow(idx)}
                          disabled={lineItems.length <= 1}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>

                      {/* Material selection grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Remove Material */}
                        <div>
                          <Label className="text-xs block mb-1">Remove Material</Label>
                          <select
                            value={row.removeMaterialId || ""}
                            onChange={(e) => handleRemoveMaterialChange(idx, e.target.value || null)}
                            disabled={row.actionType === "add"}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">Select material to remove</option>
                            {validJobMaterials
                              .filter(mat => !selectedRemoveMaterialIds.includes(mat.id) || mat.id === row.removeMaterialId)
                              .map(mat => (
                                <option key={mat.id} value={mat.id}>
                                  {mat.sku} - {mat.item_name} ({mat.quantity_required} × {(mat.prices_snapshot != null && mat.prices_snapshot > 0) ? `$${mat.prices_snapshot.toFixed(2)}` : "??"})
                                </option>
                              ))}
                          </select>
                        </div>

                        {/* Add Material */}
                        <div>
                          <Label className="text-xs block mb-1">Add Material</Label>
                          <select
                            value={row.addMaterialSku || ""}
                            onChange={(e) => handleAddMaterialChange(idx, e.target.value || null)}
                            disabled={row.actionType === "remove_only"}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">Select material to add</option>
                            {activeCatalogItems.map(item => (
                              <option key={item.sku} value={item.sku}>
                                {item.sku} - {item.itemName} ({(item.unitCost != null && item.unitCost > 0) ? `$${item.unitCost.toFixed(2)}` : "??"})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Quantity + Price comparison row */}
                      <div className="flex items-center gap-4 pt-2 border-t">
                        {/* Quantity */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Quantity</Label>
                          <Input
                            type="number"
                            min={0.5}
                            step={0.5}
                            value={row.quantity}
                            onChange={e => handleQuantityChange(idx, Number(e.target.value))}
                            className="w-20 h-8 text-sm"
                            aria-label={`Quantity for ${row.actionType === 'add' ? 'added material' : 'removed material'}`}
                          />
                          {row.actionType === "replace" && row.removeMat && row.quantity > row.removeMat.quantity_required && (
                            <span className="text-xs text-warning">Exceeds original ({row.removeMat.quantity_required})</span>
                          )}
                        </div>

                          {row.removeMat ? (
                            <>
                              <span className={priceSnapWarning ? "text-muted-foreground italic" : ""}>
                                {priceSnapWarning ? "Price unknown" : `$${(row.removeMat.prices_snapshot || 0).toFixed(2)}/unit`}
                              </span>
                              <span className="text-muted-foreground">→</span>
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                          {row.addMat ? (
                            <span className={catalogWarning ? "text-muted-foreground italic" : "font-medium"}>
                              {catalogWarning ? "No catalog price" : `$${(row.addMat.unitCost || 0).toFixed(2)}/unit`}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>

                        {/* Delta badge */}
                        {row.removeMat && row.addMat && (
                          <Badge variant={row.unitDelta >= 0 ? "success" : "destructive"} className="ml-auto">
                            {row.unitDelta >= 0 ? "+" : ""}{row.unitDelta.toFixed(2)}/unit
                          </Badge>
                        )}

                       {/* Line total (shown at bottom right) */}
                      <div className="flex justify-end">
                        <div className="text-sm">
                          <span className="text-muted-foreground">Line total: </span>
                          <span className={`font-bold ${row.amount >= 0 ? "text-success" : "text-destructive"}`}>
                            {row.amount >= 0 ? "+" : ""}${row.amount.toFixed(2)}
                          </span>
                         </div>
                     </div>
                    </div>
                  );
                })}
              </div>
              <Button variant="ghost" size="sm" onClick={addRow} className="mt-2 text-xs gap-1">
                <Plus className="h-3 w-3" /> Add Line Item
              </Button>
            </div>

            {/* Pricing summary with grouping */}
            <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Current Contract Value</span>
                <span className="font-medium">${currentContractValue.toLocaleString()}</span>
              </div>

                {/* Replacements group */}
                {enrichedLineItems.some(r => r.actionType === "replace" && r.removeMat && r.addMat) && (
                  <div className="rounded border p-3 bg-muted/10 space-y-2">
                    <h4 className="text-xs font-semibold">Material Replacements</h4>
                    {enrichedLineItems
                      .filter(r => r.actionType === "replace" && r.removeMat && r.addMat)
                      .map((row, idx) => (
                        <div key={row.id} className="flex justify-between text-xs pl-2">
                          <span>
                            Replace {row.removeMat?.item_name || "material"} with {row.addMat?.itemName || "new material"}
                            <span className="text-muted-foreground"> ({row.quantity} units)</span>
                          </span>
                          <span className={row.amount >= 0 ? "text-success" : "text-destructive"}>
                            {row.amount >= 0 ? "+" : ""}${row.amount.toFixed(2)}
                          </span>
                        </div>
                      ))}
                  <div className="border-t pt-2 mt-2 flex justify-between text-xs font-semibold">
                    <span>Subtotal (Replacements)</span>
                    <span className={replaceTotal >= 0 ? "text-success" : "text-destructive"}>
                      {replaceTotal >= 0 ? "+" : ""}${replaceTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

                {/* Additions group */}
                {enrichedLineItems.some(r => r.actionType === "add" && r.addMat) && (
                  <div className="rounded border p-3 bg-muted/10 space-y-2">
                    <h4 className="text-xs font-semibold">Additions</h4>
                    {enrichedLineItems
                      .filter(r => r.actionType === "add" && r.addMat)
                      .map((row, idx) => (
                        <div key={row.id} className="flex justify-between text-xs pl-2">
                          <span>
                            Add {row.addMat?.itemName || "material"}
                            <span className="text-muted-foreground"> ({row.quantity} units)</span>
                          </span>
                          <span className="text-success">+${row.amount.toFixed(2)}</span>
                        </div>
                      ))}
                  <div className="border-t pt-2 mt-2 flex justify-between text-xs font-semibold">
                    <span>Subtotal (Additions)</span>
                    <span className="text-success">+${addTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}

                {/* Removals group */}
                {enrichedLineItems.some(r => r.actionType === "remove_only" && r.removeMat) && (
                  <div className="rounded border p-3 bg-muted/10 space-y-2">
                    <h4 className="text-xs font-semibold">Removals (Credits)</h4>
                    {enrichedLineItems
                      .filter(r => r.actionType === "remove_only" && r.removeMat)
                      .map((row, idx) => (
                        <div key={row.id} className="flex justify-between text-xs pl-2">
                          <span>
                            Remove {row.removeMat?.item_name || "material"}
                            <span className="text-muted-foreground"> ({row.quantity} units)</span>
                          </span>
                          <span className="text-destructive">-${Math.abs(row.amount).toFixed(2)}</span>
                        </div>
                      ))}
                  <div className="border-t pt-2 mt-2 flex justify-between text-xs font-semibold">
                    <span>Subtotal (Removals)</span>
                    <span className="text-destructive">-${removeTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Net change */}
              <div className="border-t pt-2 flex justify-between text-sm font-bold">
                <span>Change Order Amount</span>
                <span className={changeAmount >= 0 ? "text-success" : "text-destructive"}>
                  {changeAmount >= 0 ? "+" : ""}${changeAmount.toLocaleString()}
                </span>
              </div>
              <div className="border-t pt-2 flex justify-between text-sm font-bold">
                <span>Revised Contract Value</span>
                <span className="text-primary">${revisedTotal.toLocaleString()}</span>
              </div>
            </div>

            {/* Installer payout impact — deliberately separate from the customer-
                facing pricing block above. Covers cases like extra labor days
                that don't map to a material line item. */}
            <div className="rounded-lg border p-3 space-y-2">
              <Label className="text-xs font-semibold block">Installer Payout Impact</Label>
              <p className="text-xs text-muted-foreground">
                Amount added to the installer's payout for this change order (e.g. an extra day of
                labor). Separate from the customer pricing above — leave at $0 if this change
                doesn't affect installer compensation.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  type="number"
                  step={1}
                  value={installerPayoutDelta}
                  onChange={e => setInstallerPayoutDelta(Number(e.target.value) || 0)}
                  className="w-32 h-8 text-sm"
                  aria-label="Installer payout impact"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Internal Notes</Label>
              <Textarea
                placeholder="Internal notes (not visible to customer)"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        )}

        {/* ── STEP: SIGNATURES ── */}
        {step === "signatures" && (
          <div className="space-y-5">
            <div>
              <Label className="text-xs font-semibold mb-2 block">Homeowner Signature *</Label>
              <SignatureCapture
                onCapture={(url) => setHomeownerSig(url)}
                label="Homeowner"
              />
              {homeownerSig && (
                <Badge variant="outline" className="mt-2 text-success border-success/30">
                  ✓ Homeowner signed
                </Badge>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold mb-2 block">Representative Signature (Optional)</Label>
              <SignatureCapture
                onCapture={(url) => setRepSig(url)}
                label="Representative"
              />
              {repSig && (
                <Badge variant="outline" className="mt-2 text-success border-success/30">
                  ✓ Representative signed
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* ── STEP: CONFIRM ── */}
        {step === "confirm" && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
              <h3 className="text-sm font-bold">Change Order Summary</h3>
              <p className="text-xs text-muted-foreground">{summary}</p>
              <div className="space-y-1">
                {enrichedLineItems
                  .filter(row => {
                    if (row.actionType === "replace") return row.removeMat && row.addMat;
                    if (row.actionType === "add") return row.addMat;
                    if (row.actionType === "remove_only") return row.removeMat;
                    return false;
                  })
                   .map((row, idx) => (
                     <div key={row.id} className="flex justify-between text-xs">
                      <span>{row.description} <span className="text-muted-foreground">({row.quantity} units)</span></span>
                      <span className="font-medium">
                        {row.amount >= 0 ? "+" : ""}${row.amount.toLocaleString()}
                      </span>
                    </div>
                  ))}
              </div>
              <div className="border-t pt-2 flex justify-between text-sm font-bold">
                <span>Revised Total</span>
                <span className="text-primary">${revisedTotal.toLocaleString()}</span>
              </div>
              {installerPayoutDelta !== 0 && (
                <div className="flex justify-between text-xs pt-1">
                  <span className="text-muted-foreground">Installer payout impact</span>
                  <span className="font-medium">
                    {installerPayoutDelta >= 0 ? "+" : ""}${installerPayoutDelta.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <PenTool className="h-3.5 w-3.5" />
              <span>Signatures captured. Upon confirmation the change order will be recorded and a copy sent to the homeowner.</span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step === "details" && (
            <Button onClick={() => setStep("signatures")} disabled={!canProceedToSign}>
              Continue to Signatures <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
          {step === "signatures" && (
            <>
              <Button variant="outline" onClick={() => setStep("details")}>Back</Button>
              <Button onClick={() => setStep("confirm")} disabled={!canFinalize}>
                Review & Confirm <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </>
          )}
          {step === "confirm" && (
            <>
              <Button variant="outline" onClick={() => setStep("signatures")}>Back</Button>
              <Button onClick={handleFinalize}>
                Finalize Change Order
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
